import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { toast } from 'sonner';
import { TrendingUp, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts';

const PortfolioPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeBets, setActiveBets] = useState([]);
  const [activeCivicVotes, setActiveCivicVotes] = useState([]);
  const [settledBets, setSettledBets] = useState([]);
  const [portfolioStats, setPortfolioStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPortfolioData();
    }
  }, [user]);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);

      const [betsResponse, civicResponse] = await Promise.all([
        api.profile.getBettingHistory().catch(() => ({ data: [] })),
        api.civic.getMyVotes().catch(() => ({ data: [] })),
      ]);
      const allBets = betsResponse.data || [];
      const allCivic = civicResponse.data || [];

      const activeBetsData = allBets.filter(bet => bet.status === 'placed');
      const activeCivicData = allCivic.filter(v => v.status === 'placed');
      // Recently-resolved bets — surfaced inline in Portfolio so users
      // see win/loss/refund payouts without having to dig into the
      // Profile → Betting History tab. Sort newest first, cap at 10.
      const settledBetsData = allBets
        .filter(b => ['won', 'lost', 'refunded'].includes(b.status))
        .sort((a, b) => {
          const da = new Date(a.settled_at || a.placed_at || 0).getTime();
          const dbb = new Date(b.settled_at || b.placed_at || 0).getTime();
          return dbb - da;
        })
        .slice(0, 10);

      setActiveBets(activeBetsData);
      setActiveCivicVotes(activeCivicData);
      setSettledBets(settledBetsData);

      const totalInvested = activeBetsData.reduce((sum, bet) => sum + Number(bet.stake || 0), 0);
      const totalPotentialReturn = activeBetsData.reduce((sum, bet) => sum + Number(bet.potential_payout || 0), 0);
      const potentialProfit = totalPotentialReturn - totalInvested;

      setPortfolioStats({
        totalInvested,
        totalPotentialReturn,
        potentialProfit,
        activePositions: activeBetsData.length,
        activeCivicVotes: activeCivicData.length,
      });

    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
            <h2 className="text-2xl font-bold mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4 dark:text-gray-400">Please login to view your portfolio</p>
            <Link to="/login">
              <Button>Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <TrendingUp className="h-12 w-12 text-emerald-600 mx-auto animate-spin dark:text-emerald-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '–';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '–';
      // Deutsche Datums-Lokalisierung: "26. Mai 2026, 17:00"
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '–';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Portfolio Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">{t('portfolio.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('portfolio.subtitle')}</p>
          </div>

          {/* Portfolio Stats — Potential Return/Profit fielen raus,
              da bei parimutuel-Märkten vor Settlement unzuverlässig
              (Pools verschieben sich, anyway-Anzeige war oft kleiner
              als Stake). Active Positions + Total Invested reichen. */}
          {portfolioStats && (
            <div className="grid grid-cols-2 gap-4 md:gap-6 mb-8 max-w-md">
              <Card className="dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm">
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {portfolioStats.activePositions}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('portfolio.activePositions')}</p>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm">
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {portfolioStats.totalInvested.toLocaleString()} VC
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('portfolio.totalInvested')}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Active Positions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {t('portfolio.activePositions')}
              </CardTitle>
              <CardDescription>
                {t('portfolio.activeDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeBets.length > 0 ? (
                <div className="space-y-4">
                  {activeBets.map((bet) => (
                    <div key={bet.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline">{bet.type}</Badge>
                            <Badge className="text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400" variant="secondary">
                              Aktiv
                            </Badge>
                            {/* Currently-winning indicator from backend.
                                Parimutuel: tells the user whether their
                                side is currently leading the pool.
                                Outcome can flip until settlement. */}
                            {bet.currently_winning ? (
                              <Badge className="text-emerald-700 bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
                                ↑ Aktuell vorne
                              </Badge>
                            ) : (
                              <Badge className="text-amber-700 bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-300">
                                ↓ Underdog
                              </Badge>
                            )}
                          </div>

                          {bet.legs.map((leg, index) => (
                            <div key={index} className="mb-3">
                              <p className="font-medium text-gray-900 dark:text-gray-100">{leg.market_title}</p>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-600 dark:text-gray-400">
                                <span>
                                  {t('portfolio.position')}: <span className="font-medium text-gray-900 dark:text-gray-100">{leg.outcome_label}</span>
                                </span>
                                <span>
                                  {t('portfolio.entry')}: <span className="font-medium">{leg.locked_price != null ? `${(leg.locked_price * 100).toFixed(1)}%` : '—'}</span>
                                </span>
                                {leg.current_user_side_probability !== undefined && (
                                  <span>
                                    {t('portfolio.current')}: <span className="font-medium">{(leg.current_user_side_probability * 100).toFixed(1)}%</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}

                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('portfolio.placedAt')} {formatDate(bet.placed_at)}
                          </div>
                        </div>

                        <div className="text-left sm:text-right">
                          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {bet.stake} VC
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">{t('portfolio.invested')}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">{t('portfolio.noActive')}</h3>
                  <p className="text-gray-500 mb-6 dark:text-gray-400">
                    You don't have any active bets. Start trading to build your portfolio!
                  </p>
                  <Link to="/markets">
                    <Button>Browse Markets</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Civic Votes — same shape as Active Positions but no
              stake/payout. Civic markets are vote-based, not money-based,
              so we list them as their own section instead of mixing
              numbers with bets. */}
          {activeCivicVotes.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                  Aktive Civic-Stimmen
                </CardTitle>
                <CardDescription>
                  Civic-Märkte, bei denen du abgestimmt hast und die noch offen sind
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeCivicVotes.map((v) => (
                    <div key={v.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300">civic</Badge>
                            <Badge className="text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400" variant="secondary">Aktiv</Badge>
                            {v.civic_category && (
                              <Badge variant="outline" className="text-[10px]">{v.civic_category}</Badge>
                            )}
                          </div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{v.market_title}</p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-600 mt-1 dark:text-gray-400">
                            <span>
                              Stimme: <span className="font-medium text-gray-900 dark:text-gray-100">
                                {v.direction === 'yes' ? 'JA' : 'NEIN'}
                              </span>
                            </span>
                            <span>
                              Aktuell: <span className="font-medium">{v.yes_percent}% JA</span>
                            </span>
                            <span>{v.total_votes} Stimmen</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-2 dark:text-gray-400">
                            Abgegeben {v.updated_at && new Date(v.updated_at).toLocaleString('de-DE')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recently Settled — won/lost/refunded bets surfaced inline
              so users see resolution outcomes in their main Portfolio
              view, not buried in Profile → Betting History. */}
          {settledBets.length > 0 && (
            <Card className="mt-6" data-testid="settled-bets-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  Kürzlich entschieden
                </CardTitle>
                <CardDescription>
                  Die letzten {settledBets.length} Ergebnisse</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {settledBets.map((bet) => {
                    const isWon = bet.status === 'won';
                    const isLost = bet.status === 'lost';
                    const isRefunded = bet.status === 'refunded';
                    const stake = Number(bet.stake || 0);
                    // Use the actual settlement payout when available
                    // (parimutuel payouts diverge from the at-bet-time
                    // potential_payout because pools shift between
                    // place and settle).
                    const payout = Number(bet.payout ?? bet.potential_payout ?? 0);
                    const pnl = isWon ? payout - stake : isLost ? -stake : 0;

                    return (
                      <div
                        key={bet.id}
                        className={`border rounded-lg p-4 ${
                          isWon
                            ? 'border-emerald-200 bg-emerald-50/30'
                            : isLost
                            ? 'border-rose-200 bg-rose-50/30'
                            : 'border-amber-200 bg-amber-50/30'
                        }`}
                        data-testid={`settled-bet-${bet.id}`}
                      >
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline">{bet.type}</Badge>
                              {isWon && (
                                <Badge className="text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-400">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Won
                                </Badge>
                              )}
                              {isLost && (
                                <Badge className="text-rose-700 bg-rose-100 border-rose-300 dark:text-rose-400">
                                  <XCircle className="h-3 w-3 mr-1" /> Lost
                                </Badge>
                              )}
                              {isRefunded && (
                                <Badge className="text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300">
                                  <RotateCcw className="h-3 w-3 mr-1" /> Refunded
                                </Badge>
                              )}
                            </div>
                            {bet.legs && bet.legs.map((leg, i) => (
                              <div key={i} className="mb-1">
                                <p className="font-medium text-gray-900 truncate dark:text-gray-100">{leg.market_title}</p>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  Position: <span className="font-medium text-gray-900 dark:text-gray-100">{leg.outcome_label}</span>
                                </div>
                              </div>
                            ))}
                            <div className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                              Settled {formatDate(bet.settled_at || bet.placed_at)}
                            </div>
                          </div>
                          <div className="text-left sm:text-right shrink-0">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Einsatz</div>
                            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{stake.toFixed(0)}c</div>
                            {isWon && (
                              <>
                                <div className="text-sm text-emerald-700 mt-1 dark:text-emerald-400">Auszahlung</div>
                                <div className="text-base font-bold text-emerald-700 dark:text-emerald-400">+{payout.toFixed(0)}c</div>
                                <div className="text-xs text-emerald-600 dark:text-emerald-400">+{pnl.toFixed(0)} profit</div>
                              </>
                            )}
                            {isLost && (
                              <>
                                <div className="text-sm text-rose-700 mt-1 dark:text-rose-400">Verlust</div>
                                <div className="text-base font-bold text-rose-700 dark:text-rose-400">{pnl.toFixed(0)}c</div>
                              </>
                            )}
                            {isRefunded && (
                              <>
                                <div className="text-sm text-amber-700 mt-1 dark:text-amber-300">Erstattet</div>
                                <div className="text-base font-bold text-amber-700 dark:text-amber-300">{stake.toFixed(0)}c</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-center">
                  <Link to="/profile" className="text-sm text-emerald-600 hover:underline dark:text-emerald-400">
                    Vollständige History ansehen →
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          {activeBets.length > 0 && (
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center gap-4">
                <Link to="/markets">
                  <Button variant="outline">Find More Markets</Button>
                </Link>
                <Link to="/profile">
                  <Button variant="ghost">View Full History</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;
